import LocalesMenu, { Translations } from "../menu/locales"
import React, { ReactNode } from "react"

import { A } from "../../utils/router"
import Cart from "../aside/cart"
import HeaderStyles from "./header.module.css"
import { Image } from "../universal/image"
import Popper from "../universal/popper"
import classNames from "classnames"
import { useAuthState } from "../../context/auth"
import { usePopperState } from "../../context/popper"
import { useQueryCart } from "../../hooks/cart/use-query-cart"
import { useQueryParams } from "../../hooks/use-url-search-params"
import { useTranslation } from "react-i18next"

type HeaderProps = {
    menu?: boolean | ReactNode
    translations?: Translations
    configuration?: {
        account?: boolean
        cart?: boolean
        shop?: boolean | string
        language?: boolean
        logoLink?: string
        customTitle?: string | false
        customTitleLink?: string | false
    }
}

const Header = ({
    menu,
    translations,
    configuration = {
        account: true,
        cart: true,
        shop: true,
        language: true,
        logoLink: process.env.WEB_URL,
        customTitle: false,
        customTitleLink: false,
    },
}: HeaderProps) => {
    const { t } = useTranslation("shared")

    const { isCustomer } = useAuthState()
    const { showPopper } = usePopperState()

    const { getQueryParam } = useQueryParams()
    const return_url = getQueryParam("return_url")
    const hasPartnerUrlInside = return_url.includes("partner")

    const { data: cartData } = useQueryCart({ background: true, enabled: !!configuration.cart })
    const amount = cartData ? cartData.items.reduce((a, b) => a + b.quantity, 0) : 0

    return (
        <header
            className={HeaderStyles.header}
            id="header"
        >
            <a href="#main" className={HeaderStyles.skipLink}>
                {t("header_skip_navigation")}
            </a>
            <div className={HeaderStyles.container}>
                <div className={HeaderStyles.left}>
                    {menu}
                    {configuration.customTitle && !configuration.customTitleLink && (
                        <span className={HeaderStyles.title}>{configuration.customTitle}</span>
                    )}
                    {configuration.customTitleLink && configuration.customTitle && (
                        <A href={configuration.customTitleLink} className={HeaderStyles.titleLink}>
                            <i className="lavita-icon"></i>
                            <span>{configuration.customTitle}</span>
                        </A>
                    )}
                </div>
                <A href={configuration.logoLink} className={HeaderStyles.logo}>
                    <Image
                        url="assets"
                        path="/logo/LaVita_Logo_2020.png"
                        srcSet={[
                            { w: 60 },
                            { w: 63 },
                            { w: 76 },
                            { w: 90, default: true },
                            { w: 120 },
                            { w: 126 },
                            { w: 152 },
                            { w: 180 },
                        ]}
                        sizes="(max-width: 389px) 60px, (max-width: 767px) 63px, (max-width: 1365px) 76px, 90px"
                        alt={t("header_logo_alt")}
                    />
                </A>
                <div className={HeaderStyles.right}>
                    {configuration.shop && (
                        <A href={process.env.SHOP_URL} className={classNames(HeaderStyles.link, HeaderStyles.shop)}>
                            <span>
                                {typeof configuration.shop === "string" ? configuration.shop : t("header_shop")}
                            </span>
                        </A>
                    )}
                    {configuration.account && (
                        <A
                            href={process.env.ACCOUNT_URL}
                            className={HeaderStyles.link}
                            aria-label={t("header_account")}
                        >
                            <i
                                className={classNames("lavita-icon", {
                                    [HeaderStyles.loginIdentifier]: isCustomer,
                                })}
                            >
                                
                            </i>
                        </A>
                    )}
                    {configuration.cart && (
                        <>
                            {amount === 0 ? (
                                <A
                                    href={process.env.SHOP_URL}
                                    className={HeaderStyles.link}
                                    aria-label={t("header_cart")}
                                >
                                    <i className="lavita-icon"></i>
                                </A>
                            ) : (
                                <Popper
                                    modifiers={{ name: "offset", options: { offset: [28, 27] } }}
                                    width={320}
                                    customInput={({ ref, onClick, triggerId, contentId }) => (
                                        <button
                                            type="button"
                                            ref={ref}
                                            onClick={onClick}
                                            className={HeaderStyles.button}
                                            id={triggerId}
                                            aria-expanded={showPopper}
                                            aria-controls={contentId}
                                            aria-haspopup="dialog"
                                            aria-label={t("header_cart")}
                                        >
                                            <i className="lavita-icon">
                                                {amount !== 0 && <div className={HeaderStyles.badge}>{amount}</div>}
                                            </i>
                                        </button>
                                    )}
                                >
                                    {closePopper => <Cart closePopper={closePopper} />}
                                </Popper>
                            )}
                        </>
                    )}
                    {!hasPartnerUrlInside && configuration.language && <LocalesMenu translations={translations} />}
                </div>
            </div>
        </header>
    )
}

export default Header
